/**
 * `/map` and `/t/{tileId}` — the 2D canvas with browse, draw, playback and
 * report modes (spec §2.2, §2.3, §2.10, §5).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isTileToken, pixelFromLatLng, tileCenterLatLng, tileFromToken, tileToken } from '@drawearth/geo';
import { dayKey, type InkState, type ReportReason, type TileRegion } from '@drawearth/shared';
import { MapView } from '../components/MapView.tsx';
import { DrawTray, Icon, PlaybackTray, ReportDialog, TopBar } from '../components/Widgets.tsx';
import { REPLAY_ENABLED, WS_URL } from '../config.ts';
import { api, ApiError } from '../lib/api.ts';
import { drawingAvailable } from '../lib/native.ts';
import { Painter } from '../lib/painter.ts';
import { refreshUser, startPositionWatch } from '../lib/session.ts';
import { appStore, toast, useApp } from '../lib/store.ts';
import { TileStore } from '../lib/tiles.ts';
import { RendererClient } from '../render/client.ts';
import { FULL_RES_MIN_ZOOM } from '../render/mercator.ts';

const DEFAULT_VIEW = { lat: 20, lng: 0, zoom: 2 };

function errorMessage(code: string, message?: string): string {
  switch (code) {
    case 'insufficient_ink':
      return 'Out of ink. It refills one drop a minute.';
    case 'out_of_range':
      return 'That pixel is outside your circle. Walk closer.';
    case 'gps_accuracy':
      return 'GPS is too fuzzy right now. Try somewhere with open sky.';
    case 'mock_location':
      return 'Mock locations are not allowed.';
    case 'implausible_speed':
      return 'You moved too fast since your last paint.';
    case 'rate_limited':
      return 'Easy. Give it a second.';
    case 'not_registered':
      return 'Sign in to paint.';
    case 'network':
      return 'No connection. Your pixels will send when it is back.';
    default:
      return message ? `${code}: ${message}` : code;
  }
}

export function MapPage() {
  const { tileId } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const mode = useApp((s) => s.mode);
  const user = useApp((s) => s.user);
  const params = useApp((s) => s.params);
  const position = useApp((s) => s.position);
  const positionError = useApp((s) => s.positionError);
  const playbackDate = useApp((s) => s.playbackDate);
  const excludeAgents = useApp((s) => s.excludeAgents);

  const renderer = useMemo(() => new RendererClient(), []);
  const store = useMemo(() => new TileStore(api, renderer, WS_URL, params.max_tile_subscriptions), [renderer, params.max_tile_subscriptions]);
  const [ink, setInk] = useState<InkState | null>(user?.ink ?? null);
  const [error, setError] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const painter = useMemo(
    () =>
      new Painter(api, store, {
        onInk: (i) => setInk(i),
        onError: (code, msg) => {
          setError(errorMessage(code, msg));
          toast(errorMessage(code, msg));
          setTimeout(() => setError(null), 4000);
        },
      }),
    [store],
  );
  const [dates, setDates] = useState<{ firstDate: string | null; dates: string[] }>({ firstDate: null, dates: [] });
  const [hover, setHover] = useState<{ tile: string; idx: number; colour: number } | null>(null);
  const [report, setReport] = useState<{ tile: string; region: TileRegion } | null>(null);
  const [locateSignal, setLocateSignal] = useState(0);
  // Replay is hidden for now; ?replay=1 still reaches it.
  const replayOffered = REPLAY_ENABLED || search.get('replay') === '1';

  // Initial view: deep link tile, query params, or the world.
  const initial = useMemo(() => {
    if (tileId && isTileToken(tileId)) {
      const c = tileCenterLatLng(tileFromToken(tileId));
      return { lat: c.lat, lng: c.lng, zoom: 17 };
    }
    const lat = Number(search.get('lat'));
    const lng = Number(search.get('lng'));
    const z = Number(search.get('z'));
    if (Number.isFinite(lat) && Number.isFinite(lng) && search.get('lat') !== null) return { lat, lng, zoom: Number.isFinite(z) && z > 0 ? z : 17 };
    return DEFAULT_VIEW;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileId]);
  const [zoom, setZoom] = useState(initial.zoom);
  const viewRef = useRef<{ lat: number; lng: number; zoom: number }>(initial);

  useEffect(() => {
    painter.radiusM = params.draw_radius_m;
    painter.maxOpsPerRequest = params.max_ops_per_request;
  }, [painter, params]);

  useEffect(() => {
    painter.setPosition(position);
  }, [painter, position]);

  useEffect(() => {
    setInk(user?.ink ?? null);
  }, [user]);

  // Dispose after a tick so React StrictMode's simulated unmount/remount in dev
  // does not kill the worker that the memoised client keeps using.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      appStore.set({ mode: 'browse', playbackDate: null });
      setTimeout(() => {
        if (mounted.current) return;
        store.dispose();
        renderer.terminate();
      }, 0);
    };
  }, [store, renderer]);

  // Playback: switch the store's data source.
  useEffect(() => {
    if (mode === 'playback' && playbackDate) store.setMode({ kind: 'playback', date: playbackDate, excludeAgents });
    else if (store.getMode().kind !== 'live') store.setMode({ kind: 'live' });
  }, [store, mode, playbackDate, excludeAgents]);

  // Start watching for a position as soon as the map opens, not only when
  // someone taps Paint, so "go to my position" is there when they look for it.
  useEffect(() => {
    void startPositionWatch();
  }, []);

  const enterDraw = async (): Promise<void> => {
    if (!user) {
      navigate('/signin?next=' + encodeURIComponent(location.pathname + location.search));
      return;
    }
    await startPositionWatch();
    appStore.set({ mode: 'draw' });
    setError(null);
    if (viewRef.current.zoom < 18) setLocateSignal((n) => n + 1);
  };

  const enterPlayback = async (): Promise<void> => {
    const v = viewRef.current;
    if (v.zoom < FULL_RES_MIN_ZOOM) {
      toast('Zoom in to replay a place.');
      return;
    }
    const t = tileToken(pixelFromLatLng(v.lat, v.lng).tile);
    try {
      const d = await api.dates(t);
      setDates({ firstDate: d.firstDate, dates: d.dates });
    } catch {
      setDates({ firstDate: null, dates: [] });
    }
    appStore.set({ mode: 'playback', playbackDate: dayKey(Date.now()) });
    if (user) void api.meta({ type: 'playback' }).catch(() => undefined);
  };

  const onRegionSelected = useCallback((tile: string, region: TileRegion) => setReport({ tile, region }), []);

  const submitReport = async (reason: ReportReason, note: string): Promise<void> => {
    if (!report) return;
    try {
      await api.report(report.tile, report.region, reason, note || undefined);
      toast('Report sent. A moderator will take a look.');
      setReport(null);
      appStore.set({ mode: 'browse' });
    } catch (err) {
      toast(err instanceof ApiError ? errorMessage(err.code, err.message) : 'The report did not send. Try again.');
    }
  };

  const onViewChange = useCallback(
    (v: { lat: number; lng: number; zoom: number }) => {
      viewRef.current = v;
      setZoom(v.zoom);
      if (!tileId) setSearch({ lat: v.lat.toFixed(6), lng: v.lng.toFixed(6), z: v.zoom.toFixed(2) }, { replace: true });
    },
    [setSearch, tileId],
  );

  const drawable = drawingAvailable();
  const live = !!user?.liveUntil && user.liveUntil > Date.now();
  const setLive = async (v: boolean): Promise<void> => {
    try {
      await api.setLive(v);
      await refreshUser();
      toast(v ? 'You are live: your avatar shows where you paint for the next 4 hours.' : 'You are hidden again.');
    } catch {
      toast('Could not change that right now.');
    }
  };
  const drawStatus = error ?? (position ? (zoom < FULL_RES_MIN_ZOOM ? 'Zoom in to paint' : `GPS ±${Math.round(position.accuracy)} m · paint inside the circle`) : positionError ? `Location: ${positionError}` : 'Finding you…');

  return (
    <div className="page page--canvas">
      <TopBar
        right={
          <>
            {mode === 'browse' && (
              <>
                {replayOffered && (
                  <button type="button" className={drawable ? 'btn btn--paper' : 'btn'} onClick={() => void enterPlayback()} data-testid="playback-btn">
                    <Icon name="play" /> Replay
                  </button>
                )}
                {user && (
                  <button type="button" className="btn btn--paper btn--icon" onClick={() => appStore.set({ mode: 'report' })} data-testid="report-btn" aria-label="Report a patch">
                    <Icon name="flag" />
                  </button>
                )}
              </>
            )}
            {mode === 'report' && (
              <button type="button" className="btn btn--paper" onClick={() => appStore.set({ mode: 'browse' })}>
                <Icon name="close" /> Cancel
              </button>
            )}
          </>
        }
      />
      <MapView store={store} renderer={renderer} painter={painter} center={initial} zoom={initial.zoom} locateSignal={locateSignal} onViewChange={onViewChange} onRegionSelected={onRegionSelected} onHoverPixel={setHover} />
      {mode === 'browse' && (
        <div className="map-cta">
          {drawable ? (
            <button type="button" className="btn btn--big" onClick={() => void enterDraw()} data-testid="draw-btn">
              <Icon name="brush" /> Paint
            </button>
          ) : (
            <Link className="btn btn--big btn--paper" to="/#get-the-app">
              Get the app to paint
            </Link>
          )}
        </div>
      )}
      {mode !== 'playback' && (
        <div className="map-fabs">
          {/* Always present so it does not come and go with the position fix;
              disabled until one arrives. */}
          <button
            type="button"
            className="btn btn--paper btn--icon"
            onClick={() => setLocateSignal((n) => n + 1)}
            disabled={!position}
            aria-label={position ? 'Go to my position' : 'Waiting for your position'}
            title={position ? 'Go to my position' : 'Waiting for your position'}
            data-testid="locate"
          >
            <Icon name="locate" />
          </button>
        </div>
      )}
      {mode === 'draw' && (
        <DrawTray
          ink={ink}
          error={!!error}
          live={live}
          onLive={(v) => void setLive(v)}
          canUndo={canUndo || painter.canUndo()}
          status={drawStatus}
          onUndo={() => {
            void painter.undo().then(() => setCanUndo(painter.canUndo()));
          }}
          onExit={() => {
            void painter.flush().then(() => refreshUser());
            painter.clearUndo();
            appStore.set({ mode: 'browse' });
          }}
        />
      )}
      {mode === 'playback' && playbackDate && (
        <PlaybackTray
          firstDate={dates.firstDate}
          dates={dates.dates}
          value={playbackDate}
          onChange={(d) => appStore.set({ playbackDate: d })}
          excludeAgents={excludeAgents}
          onExcludeAgents={(v) => appStore.set({ excludeAgents: v })}
          onExit={() => appStore.set({ mode: 'browse', playbackDate: null })}
        />
      )}
      {mode === 'report' && <div className="chip-hint">Drag a box around the pixels to report</div>}
      {mode !== 'playback' && zoom >= 17 && hover && (
        <div className="chip-hint chip-hint--corner">
          tile {hover.tile}
          {mode === 'draw' ? ` · px ${hover.idx}` : ''}
        </div>
      )}
      {report && <ReportDialog tile={report.tile} region={report.region} onSubmit={submitReport} onCancel={() => setReport(null)} />}
    </div>
  );
}
